import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import useMediaQuery from "@mui/material/useMediaQuery";
import { generateAndExecuteMutation, graphqlQuery } from "../../../../data/Axios/DynamicService";
import Header from "../../../../components/Header";
import { useEffect, useState } from "react";
import { AccountsandFinanceurl } from "../../../../config";
import { fixedAssetsCategories } from "../../../../data/Columns/Queries/FixedAssetManagement/fixedAssetsmanagement";

const base_url = AccountsandFinanceurl.uri;

const AssetTypeForm = (props) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const isNonMobile = useMediaQuery("(min-width:600px)");

  // Fetch asset categories for dropdown
  useEffect(() => {
    async function fetchCategories() {
      try {
        const categoryData = await graphqlQuery(fixedAssetsCategories, base_url);
        if (categoryData) {
          setCategories(categoryData);
          console.log("Categories loaded:", categoryData);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchCategories();
    
  }, []);

  

  const categoryOptions = categories
    ? categories.map((cat) => ({
        value: cat.id,
        label: cat.categoryName,
      }))
    : [];
    

  const initialValues = {
    typeName: props.data?.typeName || "",
    description: props.data?.description || "",
    categoryId: (props.data?.categoryId ?? "").toString(),
  };

  const validationSchema = yup.object().shape({
    typeName: yup.string().required("Type Name is required"),
    description: yup.string().required("Description is required"),
    // FIX 1: Changed from yup.number() to yup.string()
    // This accepts the initial "" and string values from the Select component.
    categoryId: yup.string().required("Category is required"),
  });

  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const userIdFromSession = parseInt(decodedToken.Id);

  const handleFormSubmit = async (values, { setSubmitting }) => {
    try {
      const { typeName, description, categoryId } = values;
      // This is still correct: we parse it to an int *before* sending.
      const parsedId = parseInt(categoryId);

      // 2. CHECK FOR NaN:
      // If parseInt failed (e.g., from ""), parsedId will be NaN.
      // In that case, send `null` to the backend. Otherwise, send the parsed number.
      const numericCategoryId = isNaN(parsedId) ? null : parsedId;
      

      const mutationData = props.isEditing
        ? {
            id: props.data.id,
            typeName,
            description,
            categoryId: numericCategoryId,
            updatedBy: userIdFromSession,
            isActive: true,
          }
        : {
            typeName,
            description,
            categoryId: numericCategoryId,
            createdBy: userIdFromSession,
            isActive: true,
          };

      console.log("Submitting data:", mutationData);

      const response = await generateAndExecuteMutation(
        props.isEditing ? "updateFixedAssetType" : "createFixedAssetType",
        mutationData,
        props.isEditing ? "updatedType" : "newType",
        base_url
      );

      if (response && Object.values(response)[0] != null) {
        props.onClose?.();
        props.refetchData?.();
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Asset Type ${props.isEditing ? "updated" : "created"} successfully.`,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed!",
          text: "Server did not return expected data. Please check API response.",
        });
      }
    } catch (error) {
      console.error("Asset Type creation/update error:", error);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    } finally {
      // FIX 2: Moved setSubmitting(false) to a 'finally' block.
      // This guarantees the button is re-enabled even if the 'try' block fails.
      setSubmitting(false);
    }
  };

  return (
    <Box m="20px">
      <Header
        title={props.isEditing ? "Edit Asset Type" : "Create Asset Type"}
        subtitle={
          props.isEditing
            ? "Edit an existing asset type record"
            : "Create a new asset type record"
        }
      />

      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              sx={{
                "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
              }}
            >
              {/* Type Name */}
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Type Name"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.typeName}
                name="typeName"
                error={touched.typeName && !!errors.typeName}
                helperText={touched.typeName && errors.typeName}
                sx={{ gridColumn: "span 4" }}
              />

              {/* Description */}
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Description"
                multiline
                rows={3}
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.description}
                name="description"
                error={touched.description && !!errors.description}
                helperText={touched.description && errors.description}
                sx={{ gridColumn: "span 4" }}
              />

              {/* Category */}
              <FormControl
                fullWidth
                variant="filled"
                error={touched.categoryId && !!errors.categoryId}
                sx={{ gridColumn: "span 4" }}
              >
                <InputLabel id="category-label">Category</InputLabel>
                <Select
                  labelId="category-label"
                  value={values.categoryId}
                  label="Category"
                  name="categoryId"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    Select Category
                  </MenuItem>
                  {categoryOptions.map((option) => (
                    <MenuItem key={option.value} value={String(option.value)}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {touched.categoryId && errors.categoryId && (
                  <FormHelperText>{errors.categoryId}</FormHelperText>
                )}
              </FormControl>
            </Box>

            <Box display="flex" justifyContent="end" mt="20px">
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default AssetTypeForm;